<template>
  <div
    ref="toolbarRef"
    class="h-12 xl:h-14 toolbar px-2 flex items-center gap-4 border-b dark:border-surface-700"
  >
    <button
      :disabled="!canUndo"
      class="toolbar-item spaced"
      :class="canUndo ? '' : 'opacity-70'"
      aria-label="Undo"
      @click="editor.dispatchCommand(UNDO_COMMAND, undefined)"
    >
      <i class="pi pi-undo" />
    </button>
    <button
      :disabled="!canRedo"
      class="toolbar-item spaced"
      :class="canRedo ? '' : 'opacity-70'"
      aria-label="Redo"
      @click="editor.dispatchCommand(REDO_COMMAND, undefined)"
    >
      <i class="pi pi-refresh" />
    </button>
    <Divider />
    <div class="w-32">
      <BlockFormatDropDown :block-type="blockType" :editor="editor" />
    </div>
    <Divider />

    <div
      class="flex items-center gap-4 [&>button]:h-8 [&>button]:w-8 [&>button]:rounded-md [&>button]:border dark:[&>button]:border-surface-700 [&>.active]:border-primary dark:[&>.active]:border-primary"
    >
      <button
        :class="`toolbar-item spaced ${isBold ? 'active' : ''}`"
        aria-label="Format Bold"
        type="button"
        @click="editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')"
      >
        <span class="font-extrabold text-lg">B</span>
      </button>
      <button
        :class="`toolbar-item spaced ${isItalic ? 'active' : ''}`"
        type="button"
        aria-label="Format Italics"
        @click="editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')"
      >
        <span class="italic font-medium text-lg font-pt-mono">I</span>
      </button>
      <button
        :class="`toolbar-item spaced ${isUnderline ? 'active' : ''}`"
        type="button"
        aria-label="Format Underline"
        @click="editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')"
      >
        <span class="font- text-lg underline">U</span>
      </button>
      <button
        :class="`toolbar-item spaced ${isStrikethrough ? 'active' : ''}`"
        type="button"
        aria-label="Format Strikethrough"
        @click="editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')"
      >
        <span class="line-through">S</span>
      </button>

      <!-- <button @click="clearFormatting" type="button" aria-label="Format Clear Formatting">
        <i class="pi pi-trash" />
      </button> -->
      <!-- <button
        :class="`toolbar-item spaced ${isLink ? 'active' : ''}`"
        type="button"
        aria-label="Insert Link"
        @click="insertLink"
      >
        <i class="pi pi-link" />
      </button> -->
    </div>
  </div>
</template>

<script setup lang="ts">
import type { CommandListenerPriority, ElementFormatType } from "lexical";
import {
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isRootOrShadowRoot,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_NORMAL,
  FORMAT_TEXT_COMMAND,
  KEY_MODIFIER_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
} from "lexical";
import {
  $getSelectionStyleValueForProperty,
  $isParentElementRTL,
} from "@lexical/selection";
import {
  $findMatchingParent,
  $getNearestNodeOfType,
  mergeRegister,
} from "@lexical/utils";
import { useLexicalComposer } from "lexical-vue";
import { onMounted, onUnmounted, ref } from "vue";
import { $isListNode, ListNode } from "@lexical/list";
import { $isHeadingNode } from "@lexical/rich-text";
import { $isCodeNode, CODE_LANGUAGE_MAP } from "@lexical/code";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import Divider from "./VerticalDivider.vue";
import BlockFormatDropDown from "./BlockFormatDropDown.vue";
import { blockTypeToBlockName, getSelectedNode, sanitizeUrl } from "./shared";

const emit = defineEmits<{
  (event: "isLinkEditMode", value: boolean): void;
}>();

const LowPriority: CommandListenerPriority = 1;

const toolbarRef = ref<HTMLDivElement | null>(null);
const editor = useLexicalComposer();

const fontSize = ref("15px");
const fontColor = ref<string>("#000");
const bgColor = ref<string>("#fff");
const canUndo = ref(false);
const canRedo = ref(false);
const blockType = ref<keyof typeof blockTypeToBlockName>("paragraph");
const selectedElementKey = ref();
const codeLanguage = ref("");
const isRTL = ref(false);
const fontFamily = ref<string>("Arial");
const isLink = ref(false);
const isBold = ref(false);
const isItalic = ref(false);
const isUnderline = ref(false);
const isStrikethrough = ref(false);
const elementFormat = ref<ElementFormatType>("left");

// const isSubscript = ref(false);
// const isSuperscript = ref(false);
const isCode = ref(false);

function $updateToolbar() {
  const selection = $getSelection();
  if ($isRangeSelection(selection)) {
    const anchorNode = selection.anchor.getNode();
    let element =
      anchorNode.getKey() === "root"
        ? anchorNode
        : $findMatchingParent(anchorNode, e => {
            const parent = e.getParent();
            return parent !== null && $isRootOrShadowRoot(parent);
          });

    if (element === null) element = anchorNode.getTopLevelElementOrThrow();

    const elementKey = element.getKey();
    const elementDOM = editor.getElementByKey(elementKey);

    // Update text format
    isBold.value = selection.hasFormat("bold");
    isItalic.value = selection.hasFormat("italic");
    isUnderline.value = selection.hasFormat("underline");
    isStrikethrough.value = selection.hasFormat("strikethrough");
    isCode.value = selection.hasFormat("code");
    isRTL.value = $isParentElementRTL(selection);

    // Update links
    const node = getSelectedNode(selection);
    const parent = node.getParent();
    if ($isLinkNode(parent) || $isLinkNode(node)) isLink.value = true;
    else isLink.value = false;

    if (elementDOM !== null) {
      selectedElementKey.value = elementKey;
      if ($isListNode(element)) {
        const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode);
        const type = parentList ? parentList.getListType() : element.getListType();
        blockType.value = type;
      } else {
        const type = $isHeadingNode(element) ? element.getTag() : element.getType();
        if (type in blockTypeToBlockName)
          blockType.value = type as keyof typeof blockTypeToBlockName;

        if ($isCodeNode(element)) {
          const language = element.getLanguage() as keyof typeof CODE_LANGUAGE_MAP;
          codeLanguage.value = language ? CODE_LANGUAGE_MAP[language] || language : "";
        }
      }
    }

    // Handle buttons
    fontSize.value = $getSelectionStyleValueForProperty(selection, "font-size", "15px");
    fontColor.value = $getSelectionStyleValueForProperty(selection, "color", "#000");
    bgColor.value = $getSelectionStyleValueForProperty(
      selection,
      "background-color",
      "#fff"
    );
    fontFamily.value = $getSelectionStyleValueForProperty(
      selection,
      "font-family",
      "Arial"
    );

    let matchingParent;
    if ($isLinkNode(parent)) {
      // If node is a link, we need to fetch the parent paragraph node to set format
      matchingParent = $findMatchingParent(
        node,
        parentNode => $isElementNode(parentNode) && !parentNode.isInline()
      );
    }

    // If matchingParent is a valid node, pass it's format type
    elementFormat.value = $isElementNode(matchingParent)
      ? matchingParent.getFormatType()
      : $isElementNode(node)
        ? node.getFormatType()
        : parent?.getFormatType() || "left";
  }
}

onMounted(() => {
  const unregisterMergeListener = mergeRegister(
    editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        $updateToolbar();
      });
    }),
    editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        $updateToolbar();
        return false;
      },
      LowPriority
    ),
    editor.registerCommand(
      CAN_UNDO_COMMAND,
      (payload: boolean) => {
        canUndo.value = payload;
        return false;
      },
      LowPriority
    ),
    editor.registerCommand(
      CAN_REDO_COMMAND,
      (payload: boolean) => {
        canRedo.value = payload;
        return false;
      },
      LowPriority
    )
  );

  onUnmounted(() => {
    unregisterMergeListener();
  });
});

onMounted(() => {
  const unregister = editor.registerCommand(
    KEY_MODIFIER_COMMAND,
    payload => {
      const event: KeyboardEvent = payload;
      const { code, ctrlKey, metaKey } = event;

      if (code === "KeyK" && (ctrlKey || metaKey)) {
        event.preventDefault();
        let url: string | null;
        if (!isLink.value) {
          emit("isLinkEditMode", true);
          url = sanitizeUrl("https://");
        } else {
          emit("isLinkEditMode", false);
          url = null;
        }
        return editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
      }
      return false;
    },
    COMMAND_PRIORITY_NORMAL
  );

  onUnmounted(() => {
    unregister();
  });
});

// function insertLink() {
//   if (!isLink.value) {
//     emit("isLinkEditMode", true);
//     editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizeUrl("https://"));
//   } else {
//     emit("isLinkEditMode", false);
//     editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
//   }
// }

// function applyStyleText(styles: Record<string, string>, skipHistoryStack?: boolean) {
//   editor.update(
//     () => {
//       const selection = $getSelection();
//       if (selection !== null) $patchStyleText(selection, styles);
//     },
//     skipHistoryStack ? { tag: "historic" } : {}
//   );
// }

// function clearFormatting() {
//   editor.update(() => {
//     const selection = $getSelection();
//     if ($isRangeSelection(selection)) {
//       $selectAll();
//       selection.getNodes().forEach(node => {
//         if ($isTextNode(node)) {
//           node.setFormat(0);
//           node.setStyle("");
//           $getNearestBlockElementAncestorOrThrow(node).setFormat("");
//         }
//         if ($isDecoratorBlockNode(node)) node.setFormat("");
//       });
//     }
//   });
// }
</script>
